import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Send, ChevronRight, AlertTriangle } from 'lucide-react';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { CampaignDeliveryChart } from '@/components/campaigns/CampaignDeliveryChart';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import {
  whatsappBroadcastsService,
  Broadcast,
  BroadcastStatus,
} from '@/services/whatsapp-broadcasts.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';

const STATUS_TONE: Record<BroadcastStatus, string> = {
  DRAFT:             'bg-surface-alt text-text-muted',
  PREFLIGHT_BLOCKED: 'bg-danger-soft text-danger',
  SCHEDULED:         'bg-info-soft text-info',
  RUNNING:           'bg-success-soft text-success',
  PAUSED:            'bg-warning-soft text-warning',
  COMPLETED:         'bg-info-soft text-info',
  CANCELLED:         'bg-surface-alt text-text-muted',
  FAILED:            'bg-danger-soft text-danger',
};

function ProgressBar({ sent, total }: { sent: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((sent / total) * 100)) : 0;
  return (
    <div className="mt-1.5 w-32">
      <div className="mb-0.5 flex justify-between text-[10px] text-text-muted tabular-nums">
        <span>{sent.toLocaleString()}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
        <div
          className="h-full rounded-full bg-gold-gradient transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function BroadcastsList() {
  const [data, setData] = useState<{ items: Broadcast[]; total: number; page: number; limit: number } | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const nav = useNavigate();
  const { active: activeAccount } = useActiveAccount();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    whatsappBroadcastsService
      .list(page, 25, activeAccount?.accountUid)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page, activeAccount?.accountUid, reloadKey]);

  return (
    <div>
      <WhatsAppHeader section="campaigns"
        title="WhatsApp Bulk Broadcasts"
        description="Send approved templates to many recipients with built-in safety guards."
        actions={
          <Button iconLeft={<Plus size={14} />} onClick={() => nav('/whatsapp-account/bulk/new')}>
            New broadcast
          </Button>
        }
      />

      <div className="px-7 py-5 space-y-5">
        {loadError && !loading && (
          <LoadErrorState what="your broadcasts" onRetry={() => setReloadKey((k) => k + 1)} />
        )}

        {!loadError && data && data.items.length > 0 && <>
          <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-border bg-surface lg:grid-cols-4">
            {[['Broadcasts', data.total], ['Running on this page', data.items.filter(b => b.status === 'RUNNING').length], ['Recipients on this page', data.items.reduce((n,b) => n+b.totalRecipients,0)], ['Needs attention', data.items.filter(b => ['FAILED','PREFLIGHT_BLOCKED','PAUSED'].includes(b.status)).length]].map(([label,value]) => <div key={label} className="border-r border-border p-5 last:border-0"><div className="text-xs text-text-muted">{label}</div><div className="mt-2 text-3xl font-semibold text-text-primary tabular-nums">{Number(value).toLocaleString()}</div></div>)}
          </div>
          <CampaignDeliveryChart title="Broadcast performance · current page" sent={data.items.reduce((n,b) => n+b.sent,0)} delivered={data.items.reduce((n,b) => n+b.delivered,0)} read={data.items.reduce((n,b) => n+b.readCount,0)} failed={data.items.reduce((n,b) => n+b.failed,0)} />
        </>}
        {loading && !data && (
          <Card padded><div className="py-12 text-center text-sm text-text-muted">Loading…</div></Card>
        )}

        {!loadError && data && data.items.length === 0 && !loading && (
          <EmptyState
            icon={<Send size={28} />}
            title="No broadcasts yet"
            description="Compose your first WhatsApp broadcast to reach a curated audience with an approved template. The system enforces tier limits, opt-outs, and auto-pauses on quality drops."
            action={
              <Button iconLeft={<Plus size={14} />} onClick={() => nav('/whatsapp-account/bulk/new')}>
                New broadcast
              </Button>
            }
          />
        )}

        {!loadError && data && data.items.length > 0 && (
          <Card padded>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-2xs font-semibold uppercase tracking-wider text-text-muted">
                    <th className="px-3 py-2.5">Name</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Recipients</th>
                    <th className="px-3 py-2.5">Progress</th>
                    <th className="px-3 py-2.5">Created</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((b) => (
                    <tr
                      key={b.broadcastUid}
                      className="cursor-pointer border-b border-border/50 last:border-0 hover:bg-surface-alt/50"
                      onClick={() => nav(`/whatsapp-account/bulk/${b.broadcastUid}`)}
                    >
                      <td className="px-3 py-2.5 text-text-primary font-medium">
                        <div>{b.name}</div>
                        {b.pauseReason && (
                          <div className="mt-0.5 flex items-center gap-1 text-2xs text-warning">
                            <AlertTriangle size={10} />
                            {b.pauseReason}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${STATUS_TONE[b.status]}`}>
                          {b.status.toLowerCase().replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-text-secondary">
                        {b.totalRecipients.toLocaleString()}
                      </td>
                      <td className="px-3 py-2.5">
                        <ProgressBar sent={b.sent} total={b.totalRecipients} />
                      </td>
                      <td className="px-3 py-2.5 text-xs text-text-muted">
                        {new Date(b.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2.5 text-right text-text-muted">
                        <ChevronRight size={14} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
          </Card>
        )}
      </div>
    </div>
  );
}
